import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Check, Sparkles, Tag } from "lucide-react";
import { toast } from "sonner";

const TAGS_PREDEFINED = [
  { value: "scontato", label: "🔥 Scontato" },
  { value: "ultimi_pezzi", label: "📦 Ultimi pezzi" },
  { value: "edizione_limitata", label: "✨ Edizione limitata" },
  { value: "nuovo", label: "🆕 Nuovo arrivo" },
  { value: "best_seller", label: "⭐ Best seller" },
];

const LANES = [
  { value: "pronta_consegna", label: "🟢 Pronta consegna", hint: "Spedizione 24-72h" },
  { value: "ordinabile_breve", label: "🟡 Ordinabile 3-5gg", hint: "Spedizione entro 5 giorni" },
  { value: "prenotabile_lungo", label: "🔵 Su prenotazione", hint: "Consegna 40-60 giorni" },
];

export default function RealizedSkuWizard({ madre, open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [tessuti, setTessuti] = useState([]);
  const [gambe, setGambe] = useState([]);
  const [metalli, setMetalli] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selezioni
  const [tessuto, setTessuto] = useState(null); // {codice, codice_colore, nome_colore, nome}
  const [piede, setPiede] = useState(null);
  const [metallo, setMetallo] = useState(null);

  // Settings
  const [prezzo, setPrezzo] = useState(madre?.prezzo_base || 0);
  const [stock, setStock] = useState(0);
  const [lane, setLane] = useState("ordinabile_breve");
  const [tags, setTags] = useState([]);
  const [skuCustom, setSkuCustom] = useState("");
  const [skuPreview, setSkuPreview] = useState("");
  const [skuExists, setSkuExists] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, g, m] = await Promise.all([
          api.get("/libreria/tessuti"),
          api.get("/libreria/gambe"),
          api.get("/libreria/metalli"),
        ]);
        setTessuti(t.data || []);
        setGambe(g.data || []);
        setMetalli(m.data || []);
      } catch (e) {
        toast.error("Errore caricamento librerie: " + (e.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
  }, []);

  // Tessuti raggruppati per codice (BARREL, RIVIERA, ...)
  const tessutiByCodice = useMemo(() => {
    const groups = {};
    tessuti.forEach((t) => {
      const code = t.codice?.split("-")[0] || t.codice;
      if (!groups[code]) groups[code] = [];
      groups[code].push(t);
    });
    return groups;
  }, [tessuti]);

  const [tessutoTipo, setTessutoTipo] = useState("");

  // Calcola anteprima SKU ogni volta che cambiano le selezioni
  useEffect(() => {
    if (!madre) return;
    const sel = {};
    if (tessuto) sel.tessuto = { codice: tessuto.codice?.split("-")[0] || tessuto.codice, codice_colore: tessuto.codice_colore || "" };
    if (piede) sel.piede = { codice: piede.codice };
    if (metallo) sel.metallo = { codice: metallo.codice };

    api.post("/products/preview-sku", { madre_id: madre.id, selezioni: sel })
      .then((r) => { setSkuPreview(r.data.sku_realizzato); setSkuExists(r.data.esiste_gia); })
      .catch(() => {});
  }, [madre, tessuto, piede, metallo]);

  const prezzoFinale = useMemo(() => {
    const base = parseFloat(prezzo) || 0;
    const surchTess = parseFloat(tessuto?.sovrapprezzo || 0);
    const surchPiede = parseFloat(piede?.sovrapprezzo || 0);
    const surchMet = parseFloat(metallo?.sovrapprezzo || 0);
    return (base + surchTess + surchPiede + surchMet).toFixed(2);
  }, [prezzo, tessuto, piede, metallo]);

  const toggleTag = (t) => setTags(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);

  const submit = async () => {
    if (!prezzo || parseFloat(prezzo) <= 0) {
      toast.error("Imposta un prezzo valido");
      setStep(3);
      return;
    }
    setSaving(true);
    try {
      const selezioni = {};
      if (tessuto) selezioni.tessuto = {
        codice: tessuto.codice?.split("-")[0] || tessuto.codice,
        codice_colore: tessuto.codice_colore || "",
        nome_colore: tessuto.nome_colore || "",
      };
      if (piede) selezioni.piede = { codice: piede.codice, nome: piede.nome };
      if (metallo) selezioni.metallo = { codice: metallo.codice, nome: metallo.nome, hex_color: metallo.hex_color };

      await api.post("/realized-products", {
        madre_id: madre.id,
        selezioni,
        prezzo_realizzato: parseFloat(prezzoFinale),
        stock: parseInt(stock) || 0,
        lane, tags,
        sku_custom: skuCustom || undefined,
        visibile_in_vetrina: true,
      });
      toast.success(`SKU ${skuCustom || skuPreview} creato e visibile in vetrina`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    } finally { setSaving(false); }
  };

  const canNext = step === 1 ? true : step === 2 ? true : true;
  const canFinish = !!skuPreview && !skuExists && parseFloat(prezzo) > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-auto" data-testid="realized-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand" />
            <span>Nuovo SKU realizzato — <span className="font-mono text-sm text-muted-foreground">{madre?.sku_madre}</span> {madre?.nome}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 my-3" data-testid="wizard-stepper">
          {[1, 2, 3, 4].map((n) => (
            <React.Fragment key={n}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= n ? "bg-brand text-white" : "bg-neutral-100 text-muted-foreground"}`}>
                {step > n ? <Check className="w-4 h-4" /> : n}
              </div>
              {n < 4 && <div className={`h-px flex-1 ${step > n ? "bg-brand" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mb-4">
          {step === 1 && "Step 1/4 · Tessuto"}
          {step === 2 && "Step 2/4 · Piede & Metallo"}
          {step === 3 && "Step 3/4 · Prezzo, Stock & Disponibilità"}
          {step === 4 && "Step 4/4 · Badge & Conferma"}
        </div>

        {/* SKU preview banner sempre visibile */}
        <div className="bg-neutral-50 border border-border rounded-sm p-3 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">SKU che verrà generato</div>
          <div className="flex items-center justify-between gap-3">
            <div className="font-mono text-lg font-bold text-foreground" data-testid="wizard-sku-preview">
              {skuPreview || madre?.sku_madre?.split("-")[0] || "—"}
            </div>
            {skuExists && (
              <div className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded-sm">⚠️ Già esistente</div>
            )}
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Prezzo finale</div>
              <div className="font-bold text-lg">€ {prezzoFinale}</div>
            </div>
          </div>
        </div>

        {loading && <div className="text-center py-12 text-muted-foreground">Caricamento librerie…</div>}

        {!loading && step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">1) Tipo tessuto</label>
                <Select value={tessutoTipo || "__none__"} onValueChange={(v) => { setTessutoTipo(v === "__none__" ? "" : v); setTessuto(null); }}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="wizard-tessuto-tipo"><SelectValue placeholder="— Nessuno —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessun tessuto —</SelectItem>
                    {Object.keys(tessutiByCodice).sort().map((c) => (
                      <SelectItem key={c} value={c}>{c} ({tessutiByCodice[c].length} colori)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">2) Colore</label>
                <Select value={tessuto?.codice || ""} onValueChange={(v) => setTessuto(tessuti.find((t) => t.codice === v))} disabled={!tessutoTipo}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="wizard-tessuto-colore"><SelectValue placeholder={tessutoTipo ? "Scegli colore" : "Prima scegli tipo"} /></SelectTrigger>
                  <SelectContent>
                    {(tessutiByCodice[tessutoTipo] || []).map((t) => (
                      <SelectItem key={t.codice} value={t.codice}>
                        {t.codice} {t.nome_colore ? `· ${t.nome_colore}` : ""} {t.livello ? `(${t.livello})` : ""} {t.sovrapprezzo > 0 ? `+€${t.sovrapprezzo}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {tessuto && (
              <div className="bg-brand/10 border border-brand/30 rounded-sm p-3 text-xs">
                Tessuto selezionato: <strong>{tessuto.codice}</strong> {tessuto.nome_colore && `(${tessuto.nome_colore})`} · Livello {tessuto.livello || "—"} · Sovrapprezzo €{tessuto.sovrapprezzo || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Hai {tessuti.length} varianti tessuto in libreria. Salta questo step se il prodotto non ha tessuto (es. tavolino).</p>
          </div>
        )}

        {!loading && step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Piede / Base</label>
              <Select value={piede?.codice || "__none__"} onValueChange={(v) => setPiede(v === "__none__" ? null : gambe.find((g) => g.codice === v))}>
                <SelectTrigger className="h-10 rounded-sm" data-testid="wizard-piede"><SelectValue placeholder="— Nessuno —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessun piede —</SelectItem>
                  {gambe.map((g) => (
                    <SelectItem key={g.codice} value={g.codice}>
                      {g.nome} ({g.codice}) {g.sovrapprezzo > 0 ? ` +€${g.sovrapprezzo}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Finitura metallo</label>
              <Select value={metallo?.codice || "__none__"} onValueChange={(v) => setMetallo(v === "__none__" ? null : metalli.find((m) => m.codice === v))}>
                <SelectTrigger className="h-10 rounded-sm" data-testid="wizard-metallo"><SelectValue placeholder="— Nessuna —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessuna finitura —</SelectItem>
                  {metalli.map((m) => (
                    <SelectItem key={m.codice} value={m.codice}>
                      <span className="inline-flex items-center gap-2">
                        {m.hex_color && <span className="inline-block w-3 h-3 rounded-sm border border-border" style={{ backgroundColor: m.hex_color }} />}
                        {m.nome} ({m.codice}) {m.sovrapprezzo > 0 ? ` +€${m.sovrapprezzo}` : ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {!loading && step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Prezzo base €</label>
                <input type="number" step="0.01" value={prezzo} onChange={(e) => setPrezzo(e.target.value)}
                  data-testid="wizard-prezzo"
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
                <p className="text-[11px] text-muted-foreground mt-1">Sovrapprezzi tessuto/piede/metallo aggiunti automaticamente al totale.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Stock pz</label>
                <input type="number" value={stock} onChange={(e) => setStock(e.target.value)}
                  data-testid="wizard-stock"
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
                <p className="text-[11px] text-muted-foreground mt-1">Quantità disponibile a magazzino.</p>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Disponibilità (lane)</label>
              <div className="grid grid-cols-3 gap-2" data-testid="wizard-lane">
                {LANES.map((l) => (
                  <button key={l.value} type="button" onClick={() => setLane(l.value)}
                    className={`p-3 rounded-sm border text-left transition-colors ${lane === l.value ? "border-brand bg-brand/10" : "border-border hover:bg-accent"}`}>
                    <div className="text-sm font-medium mb-1">{l.label}</div>
                    <div className="text-[11px] text-muted-foreground">{l.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && step === 4 && (
          <div className="space-y-4" data-testid="wizard-step-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Badge / Etichette in vetrina (opzionale)
              </label>
              <div className="flex flex-wrap gap-1.5">
                {TAGS_PREDEFINED.map((t) => (
                  <button key={t.value} type="button" onClick={() => toggleTag(t.value)}
                    data-testid={`wizard-tag-${t.value}`}
                    className={`text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${tags.includes(t.value) ? "bg-foreground text-white border-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">SKU custom (opzionale, sovrascrive l&apos;auto)</label>
              <input value={skuCustom} onChange={(e) => setSkuCustom(e.target.value.toUpperCase())}
                placeholder={skuPreview} data-testid="wizard-sku-custom"
                className="w-full h-10 px-3 rounded-sm border border-border text-sm font-mono" />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm">
              <div className="font-bold mb-2">📋 Riepilogo</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Modello:</span> {madre?.nome}</div>
                <div><span className="text-muted-foreground">SKU finale:</span> <strong className="font-mono">{skuCustom || skuPreview}</strong></div>
                <div><span className="text-muted-foreground">Tessuto:</span> {tessuto ? `${tessuto.codice} ${tessuto.nome_colore || ""}` : "—"}</div>
                <div><span className="text-muted-foreground">Piede:</span> {piede ? piede.nome : "—"}</div>
                <div><span className="text-muted-foreground">Metallo:</span> {metallo ? metallo.nome : "—"}</div>
                <div><span className="text-muted-foreground">Prezzo:</span> <strong>€ {prezzoFinale}</strong></div>
                <div><span className="text-muted-foreground">Stock:</span> {stock} pz</div>
                <div><span className="text-muted-foreground">Lane:</span> {LANES.find((l) => l.value === lane)?.label}</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer navigation */}
        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-border">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} data-testid="wizard-back"
              className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent inline-flex items-center gap-1.5">
              <ChevronLeft className="w-4 h-4" /> Indietro
            </button>
          )}
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="wizard-next"
              className="ml-auto h-10 px-5 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-50 inline-flex items-center gap-1.5">
              Avanti <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={!canFinish || saving} data-testid="wizard-finish"
              className="ml-auto h-10 px-5 rounded-sm bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving ? "Creazione…" : <>Crea SKU realizzato <Check className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
